import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Logo from '../components/Logo'
import { getAnalyticsSummary } from '../api'

interface Summary {
  active_users_7d: number
  active_users_30d: number
  total_events_7d: number
  top_skills: { skill_name: string; view_count: number; edit_count: number }[]
  mcp_calls_7d: number
  recent_events: { user_name: string; event_type: string; skill_name: string; created_at: string }[]
}

const EVENT_LABELS: Record<string, string> = {
  skill_viewed: 'viewed',
  skill_edited: 'edited',
  skill_published: 'published',
  mcp_tool_called: 'used MCP tool on',
  user_login: 'logged in',
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

export default function AnalyticsPage() {
  const navigate = useNavigate()
  const [data, setData] = useState<Summary | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getAnalyticsSummary()
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const viewCount = data?.top_skills.reduce((sum, s) => sum + s.view_count, 0) || 0
  const editCount = data?.top_skills.reduce((sum, s) => sum + s.edit_count, 0) || 0

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--text1)' }}>
      {/* Nav */}
      <nav style={{ height: 52, background: 'var(--surface)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', padding: '0 20px', gap: 12, position: 'sticky', top: 0, zIndex: 10, backdropFilter: 'blur(20px)' }}>
        <div style={{ cursor: 'pointer' }} onClick={() => navigate('/')}><Logo /></div>
        <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text3)', fontSize: 12, fontFamily: 'inherit', padding: '4px 8px', borderRadius: 6 }}>
          <span style={{ fontSize: 14 }}>&larr;</span> Dashboard
        </button>
        <span style={{ color: 'var(--text3)', fontSize: 13 }}>&rsaquo;</span>
        <span style={{ color: 'var(--text1)', fontSize: 13, fontWeight: 600 }}>Analytics</span>
      </nav>

      <div style={{ maxWidth: 960, margin: '0 auto', padding: '40px 24px' }}>
        <h1 style={{ fontFamily: 'Sora, sans-serif', fontSize: 24, fontWeight: 700, letterSpacing: -0.5, marginBottom: 4 }}>Analytics</h1>
        <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 32 }}>Usage overview for the last 7 days</p>

        {loading ? (
          <div style={{ color: 'var(--text3)', fontSize: 13 }}>Loading analytics...</div>
        ) : (
          <>
            {/* Stat cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 32 }}>
              {[
                { label: 'Active users', value: data?.active_users_7d || 0, sub: `${data?.active_users_30d || 0} in 30d` },
                { label: 'Skills viewed', value: viewCount, sub: '7 day total' },
                { label: 'Skills edited', value: editCount, sub: '7 day total' },
                { label: 'MCP calls', value: data?.mcp_calls_7d || 0, sub: '7 day total' },
              ].map((stat, i) => (
                <div key={i} className="stat-card" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 18px' }}>
                  <div style={{ fontFamily: 'Sora, sans-serif', fontSize: 28, fontWeight: 700, letterSpacing: -0.5, color: 'var(--text1)' }}>{stat.value}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2 }}>{stat.label}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>{stat.sub}</div>
                </div>
              ))}
            </div>

            {/* Two columns: top skills + recent activity */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {/* Top skills */}
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Top skills</div>
                {(!data?.top_skills.length) ? (
                  <div style={{ color: 'var(--text3)', fontSize: 12 }}>No skill activity yet</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Header */}
                    <div style={{ display: 'flex', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      <div style={{ flex: 1 }}>Skill</div>
                      <div style={{ width: 60, textAlign: 'right' }}>Views</div>
                      <div style={{ width: 60, textAlign: 'right' }}>Edits</div>
                    </div>
                    {data.top_skills.map((s, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                        onClick={() => navigate(`/skills/${s.skill_name}`)}>
                        <div style={{ flex: 1, fontSize: 13, fontWeight: 500, color: 'var(--text1)' }}>{s.skill_name}</div>
                        <div style={{ width: 60, textAlign: 'right', fontSize: 12, color: 'var(--text2)' }}>{s.view_count}</div>
                        <div style={{ width: 60, textAlign: 'right', fontSize: 12, color: 'var(--text2)' }}>{s.edit_count}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent activity */}
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Recent activity</div>
                {(!data?.recent_events.length) ? (
                  <div style={{ color: 'var(--text3)', fontSize: 12 }}>No activity yet</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {data.recent_events.map((e, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--brand-dim)', border: '1px solid rgba(124,58,237,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: 'var(--brand-light)', flexShrink: 0 }}>
                          {(e.user_name || '?')[0].toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, color: 'var(--text1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <strong>{e.user_name}</strong>{' '}{EVENT_LABELS[e.event_type] || e.event_type}{' '}
                            {e.skill_name && <span style={{ color: 'var(--brand-light)' }}>{e.skill_name}</span>}
                          </div>
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--text3)', flexShrink: 0 }}>{timeAgo(e.created_at)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
